import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, Mail, Phone, User } from "lucide-react";
import {
  CONTACT_LIST_PAGE_SIZES,
  DEFAULT_CONTACT_LIST_PAGE_SIZE,
  type ContactListItem,
  type ContactListQuery,
} from "@multi-chat/shared";
import { listContacts } from "../../api/contacts-api.js";
import {
  AlertDialog,
  AlertDialogActions,
  AlertDialogDescription,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog/index.js";
import { Button } from "../../components/ui/button/index.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { cn } from "../../lib/utils.js";
import {
  contactsModalFooterClass,
  contactsModalHeaderClass,
} from "../contacts/contacts-dialog-presentation.js";
import {
  contactsBulkBarClass,
  contactsTableBodyCellClass,
  contactsTableBodyRowClass,
  contactsTableClass,
  contactsTableHeadRowClass,
  contactsTableHeaderCellClass,
  contactsTableHeaderIconWrapClass,
  contactsTableHeaderLabelClass,
  contactsTableHeaderStickyTopClass,
  contactsTableShellClass,
  contactsValueChipClass,
} from "../contacts/contacts-table-presentation.js";
import { CampaignModalContent } from "./CampaignModalContent.js";
import {
  TableWorkspace,
  TableWorkspaceFooter,
  TableWorkspaceScroll,
  tableWorkspaceHeadRowClass,
} from "../../components/common/TableWorkspace.js";
import {
  currentPageSelectionState,
  syncCheckboxIndeterminate,
  toggleCurrentPageSelection,
} from "./campaigns-presentation.js";
import { idSourceApplyCta } from "./campaign-audience-source-state.js";

type ContactPickerPageSize = (typeof CONTACT_LIST_PAGE_SIZES)[number];

export function CampaignContactPickerPanel({
  organizationId,
  selectedIds,
  onCancel,
  onConfirm,
  onReview,
  onSelectionChange,
  reviewDisabled,
  busy,
  embedded,
}: {
  organizationId: number;
  selectedIds: readonly number[];
  onCancel: () => void;
  onConfirm: (contactIds: number[]) => Promise<void>;
  onReview?: () => void;
  onSelectionChange?: (contactIds: number[]) => void;
  reviewDisabled?: boolean;
  busy?: boolean;
  embedded?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [uncontrolledIds, setUncontrolledIds] = useState<Set<number>>(() => new Set(selectedIds));
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<ContactPickerPageSize>(DEFAULT_CONTACT_LIST_PAGE_SIZE);
  const [items, setItems] = useState<ContactListItem[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (onSelectionChange) return;
    setUncontrolledIds(new Set(selectedIds));
    setSearch("");
    setDebouncedSearch("");
    setPage(1);
  }, [onSelectionChange, selectedIds]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    const query: ContactListQuery = {
      page,
      pageSize,
      search: debouncedSearch.length > 0 ? debouncedSearch : undefined,
    };
    setLoading(true);
    setError(false);
    void listContacts(organizationId, query)
      .then((response) => {
        setItems(response.items);
        setTotalPages(response.totalPages);
        setTotalCount(response.totalCount);
      })
      .catch(() => {
        setItems([]);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [debouncedSearch, organizationId, page, pageSize]);

  const draftIds = onSelectionChange ? new Set(selectedIds) : uncontrolledIds;
  const setDraftIds = (updater: Set<number> | ((current: Set<number>) => Set<number>)) => {
    const current = onSelectionChange ? new Set(selectedIds) : uncontrolledIds;
    const next = typeof updater === "function" ? updater(current) : updater;
    if (onSelectionChange) onSelectionChange([...next]);
    else setUncontrolledIds(next);
  };

  const pageSelection = currentPageSelectionState(items, draftIds);
  const setSelectPageCheckbox = useCallback(
    (node: HTMLInputElement | null) => syncCheckboxIndeterminate(node, pageSelection),
    [pageSelection],
  );

  const toggle = (id: number) => {
    setDraftIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = totalCount === 0 ? 0 : Math.min(page * pageSize, totalCount);
  const showTable = !error && !loading && items.length > 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {embedded ? null : (
        <div className={contactsModalHeaderClass}>
          <AlertDialogTitle>{t("campaigns.contacts.title")}</AlertDialogTitle>
          <AlertDialogDescription>{t("campaigns.contacts.description")}</AlertDialogDescription>
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="shrink-0 space-y-2 px-6 pb-2 pt-1">
          <label className="block space-y-1.5">
            <span className="text-[0.8125rem] font-medium">{t("campaigns.contacts.search")}</span>
            <div className="relative">
              <Search
                size={14}
                className="pointer-events-none absolute start-2.5 top-1/2 -translate-y-1/2 text-muted"
                aria-hidden="true"
              />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("campaigns.contacts.searchPlaceholder")}
                className="w-full rounded-md border border-border bg-background py-2 ps-8 pe-3 text-base sm:text-[0.8125rem]"
              />
            </div>
          </label>
          {error ? (
            <p className="m-0 text-[0.8125rem] text-error" role="alert">
              {t("campaigns.errors.loadContactsFailed")}
            </p>
          ) : null}
          {loading ? (
            <p className="m-0 text-[0.8125rem] text-muted" aria-live="polite">
              {t("campaigns.loading")}
            </p>
          ) : null}
          {!loading && !error && items.length === 0 ? (
            <div className="grid justify-items-start gap-1 py-4" data-testid="campaign-contacts-empty">
              <User size={18} className="text-muted" aria-hidden="true" />
              <p className="m-0 text-[0.875rem] font-medium">
                {debouncedSearch.length > 0
                  ? t("campaigns.contacts.noMatches")
                  : t("campaigns.contacts.empty")}
              </p>
              <p className="m-0 text-[0.75rem] text-muted">
                {debouncedSearch.length > 0
                  ? t("campaigns.contacts.noMatchesHelp")
                  : t("campaigns.contacts.emptyHelp")}
              </p>
            </div>
          ) : null}
          {showTable && draftIds.size > 0 ? (
            <div
              className={cn(contactsBulkBarClass, "rounded-lg")}
              role="region"
              aria-label={t("campaigns.contacts.selectedCount", { count: draftIds.size })}
              data-testid="campaign-contact-selection-bar"
            >
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <span className="text-[0.8125rem] font-semibold" data-testid="campaign-contact-selected-count">
                  {t("campaigns.contacts.selectedCount", { count: draftIds.size })}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8"
                  onClick={() => setDraftIds(new Set())}
                  data-testid="campaign-contact-clear"
                >
                  {t("campaigns.contacts.clear")}
                </Button>
              </div>
            </div>
          ) : null}
        </div>
        {showTable ? (
          <TableWorkspace className={cn(contactsTableShellClass, "mx-6 mb-3")}>
            <TableWorkspaceScroll data-testid="campaign-contact-picker-list">
              <table
                className={cn(contactsTableClass, "min-w-[36rem]")}
                aria-label={t("campaigns.contacts.title")}
              >
                <thead>
                  <tr className={cn(contactsTableHeadRowClass, tableWorkspaceHeadRowClass)}>
                    <th className={cn(contactsTableHeaderCellClass, contactsTableHeaderStickyTopClass, "w-10 p-0")}>
                      <label className="inline-grid size-9 cursor-pointer place-items-center">
                        <input
                          ref={setSelectPageCheckbox}
                          type="checkbox"
                          aria-label={t("campaigns.contacts.selectPage")}
                          checked={pageSelection === "all"}
                          onChange={() =>
                            setDraftIds((current) =>
                              toggleCurrentPageSelection(items, current),
                            )
                          }
                          className="size-4 accent-[var(--color-accent)]"
                        />
                      </label>
                    </th>
                    <th className={cn(contactsTableHeaderCellClass, contactsTableHeaderStickyTopClass)}>
                      <span className={contactsTableHeaderLabelClass}>
                        <span className={contactsTableHeaderIconWrapClass} aria-hidden="true">
                          <User size={12} />
                        </span>
                        {t("campaigns.contacts.columns.name")}
                      </span>
                    </th>
                    <th className={cn(contactsTableHeaderCellClass, contactsTableHeaderStickyTopClass)}>
                      <span className={contactsTableHeaderLabelClass}>
                        <span className={contactsTableHeaderIconWrapClass} aria-hidden="true">
                          <Phone size={12} />
                        </span>
                        {t("campaigns.contacts.columns.phone")}
                      </span>
                    </th>
                    <th className={cn(contactsTableHeaderCellClass, contactsTableHeaderStickyTopClass)}>
                      <span className={contactsTableHeaderLabelClass}>
                        <span className={contactsTableHeaderIconWrapClass} aria-hidden="true">
                          <Mail size={12} />
                        </span>
                        {t("campaigns.contacts.columns.email")}
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const id = item.id;
                    const checked = draftIds.has(id);
                    return (
                      <tr
                        key={id}
                        className={contactsTableBodyRowClass}
                        data-selected={checked || undefined}
                      >
                        <td className={cn(contactsTableBodyCellClass, "w-10 p-0")}>
                          <label className="inline-grid size-9 cursor-pointer place-items-center">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggle(id)}
                              aria-label={item.displayName}
                              className="size-4 accent-[var(--color-accent)]"
                            />
                          </label>
                        </td>
                        <td
                          className={cn(
                            contactsTableBodyCellClass,
                            "max-w-[14rem] truncate font-medium text-foreground",
                          )}
                          title={item.displayName}
                        >
                          {item.displayName}
                        </td>
                        <td className={contactsTableBodyCellClass}>
                          {item.normalizedPhoneNumber ? (
                            <LtrValue
                              className={cn(contactsValueChipClass, "max-w-full")}
                              title={item.normalizedPhoneNumber}
                            >
                              {item.normalizedPhoneNumber}
                            </LtrValue>
                          ) : (
                            <span className="text-subtle">{t("campaigns.contacts.noPhone")}</span>
                          )}
                        </td>
                        <td className={contactsTableBodyCellClass}>
                          {item.email ? (
                            <LtrValue
                              className={cn(contactsValueChipClass, "max-w-full")}
                              title={item.email}
                            >
                              {item.email}
                            </LtrValue>
                          ) : (
                            <span className="text-subtle">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableWorkspaceScroll>
            <TableWorkspaceFooter>
              <p className="m-0 text-[0.75rem] text-muted tabular-nums" data-testid="campaign-contact-range">
                {t("campaigns.contacts.viewing", {
                  start: rangeStart,
                  end: rangeEnd,
                  total: totalCount,
                })}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-1.5 text-[0.75rem] text-muted">
                  <span className="sr-only">{t("campaigns.pagination.pageSize")}</span>
                  <select
                    value={pageSize}
                    aria-label={t("campaigns.pagination.pageSize")}
                    className="h-8 rounded-md border border-border bg-background px-2 text-[0.75rem]"
                    onChange={(event) => {
                      setPageSize(Number(event.target.value) as ContactPickerPageSize);
                      setPage(1);
                    }}
                  >
                    {CONTACT_LIST_PAGE_SIZES.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  {t("campaigns.pagination.previous")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8"
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((current) => current + 1)}
                >
                  {t("campaigns.pagination.next")}
                </Button>
              </div>
            </TableWorkspaceFooter>
          </TableWorkspace>
        ) : null}
      </div>
      {embedded ? null : (
      <AlertDialogActions className={contactsModalFooterClass}>
        <Button type="button" variant="outline" onClick={onCancel}>
          {t("campaigns.actions.cancel")}
        </Button>
        {onReview ? (
          <Button type="button" variant="outline" disabled={reviewDisabled} onClick={onReview}>
            {t("campaigns.preview.action")}
          </Button>
        ) : null}
        <Button
          type="button"
          disabled={busy || idSourceApplyCta(selectedIds, [...draftIds]).disabled}
          data-testid="campaign-contact-add"
          onClick={() => void onConfirm([...draftIds])}
        >
          {t("campaigns.contacts.add", { count: draftIds.size })}
        </Button>
      </AlertDialogActions>
      )}
    </div>
  );
}
export function CampaignContactPickerDialog({
  open,
  organizationId,
  selectedIds,
  onOpenChange,
  onConfirm,
  busy,
}: {
  open: boolean;
  organizationId: number;
  selectedIds: readonly number[];
  onOpenChange: (open: boolean) => void;
  onConfirm: (contactIds: number[]) => Promise<void>;
  busy?: boolean;
}): React.JSX.Element {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <CampaignModalContent size="lg">
        {open ? (
          <CampaignContactPickerPanel
            organizationId={organizationId}
            selectedIds={selectedIds}
            busy={busy}
            onCancel={() => onOpenChange(false)}
            onConfirm={onConfirm}
          />
        ) : null}
      </CampaignModalContent>
    </AlertDialog>
  );
}
